import * as React from 'react';
import { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/fr';
import moment from 'moment';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { COLORS, output, dateFormatter } from '../../settings/themeSettings';
import { fetchCircuits } from '../../data/slices/circuitSlice';
import { 
    Box, 
    Typography, 
    Paper, 
    Chip, 
    Tabs, 
    Tab, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    alpha
} from '@mui/material';
import { Event as EventIcon, List as ListIcon } from '@mui/icons-material';

const localizer = momentLocalizer(moment);

const messages = {
    allDay: 'journée',
    previous: 'précédent',
    next: 'suivant',
    today: 'aujourd\'hui',
    month: 'mois',
    week: 'semaine',
    day: 'jour',
    agenda: 'Agenda',
    date: 'date',
    time: 'heure',
    event: 'événement',
    showMore: total => `+ ${total} événement(s) supplémentaire(s)`
};

const eventPropGetter = (event) => {
    let backgroundColor = COLORS.primary;
    switch (event.type) {
        case 'circuit':   backgroundColor = '#1e3a8a'; break; // Navy
        case 'sejour':    backgroundColor = '#d97706'; break; // Amber
        case 'excursion': backgroundColor = '#059669'; break; // Emerald
        case 'transfert': backgroundColor = '#7c3aed'; break; // Violet
        case 'hotel':     backgroundColor = '#db2777'; break; // Pink
        default:          backgroundColor = COLORS.primary;
    }

    return {
        style: {
            backgroundColor,
            borderRadius: '4px',
            opacity: 0.85,
            color: 'white',
            border: 'none',
            display: 'block',
            fontSize: '0.75rem',
            padding: '2px 5px'
        }
    };
};

const LogisticsView = ({ circuits }) => {
    const navigateTo = useNavigate();
    const [filters, setFilters] = useState({
        dateType: 'text',
        date: '',
        ref: '',
        agence: '',
        miseEnPlace: '',
        heure: '',
        vol: '',
        parcours: '',
        client: '',
        guide: '',
        vehicule: ''
    });
    
    // Transformer les circuits en lignes logistiques détaillées (Explosion)
    const rows = [];
    circuits.forEach(d => {
        // AGENCE : agence de location de voiture choisie lors de la création du circuit
        // d.locations = tableau des locations, chaque location a une agence (Agence.nom)
        const premiereLocation = (d.locations && d.locations.length > 0) ? d.locations[0] : null;
        const agenceInfo = premiereLocation?.agence?.nom
            || (typeof premiereLocation?.agence === 'string' ? premiereLocation.agence : '—');

        // NOM DU CLIENT :
        // - Client au comptant → d.nomClient
        // - Client TO (agence) → d.toClient.libelle
        // Tout dossier a forcément l'un ou l'autre
        const clientInfo = d.nomClient
            || d.toClient?.libelle
            || (typeof d.toClient === 'string' ? d.toClient : 'Nom non renseigné');

        const dateDebut = d.dateVolArrivee || d.dateDebut || d.date;
        const dateFin = d.dateVolDepart || d.dateFin || d.date;
        const year = dateDebut ? new Date(dateDebut).getFullYear() : null;

        // Aide pour extraire le texte d'un objet ou d'une chaine
        const getLabel = (val, prop) => {
            if (!val) return '—';
            if (typeof val === 'string') return val;
            return val[prop] || '—';
        };

        const guideLabel    = getLabel(d.guide, 'nomComplet');
        const vehiculeLabel = getLabel(d.vehicule, 'matricule');

        // Lieu d'arrivée : lieuArrivee du premier transfert, sinon 'AIBD'
        const premiereArrivee = (d.transferts && d.transferts.length > 0)
            ? (d.transferts[0].lieuArrivee || d.transferts[0].lieuDepart || 'AIBD')
            : 'AIBD';
        // Lieu de départ : lieuDepart du dernier transfert, sinon 'AIBD'
        const dernierDepart = (d.transferts && d.transferts.length > 0)
            ? (d.transferts[d.transferts.length - 1].lieuDepart || 'AIBD')
            : 'AIBD';

        // 1. Ligne d'Arrivée / Transfert-Arrivée
        if (d.dateVolArrivee || d.heureVolArrivee) {
            rows.push({
                date: dateFormatter(d.dateVolArrivee || dateDebut),
                sortDate: d.dateVolArrivee || dateDebut,
                year,
                ref: d.reference,
                agence: agenceInfo,
                miseEnPlace: premiereArrivee,
                heure: d.heureVolArrivee ? dayjs(d.heureVolArrivee).format('HH:mm') : '—',
                vol: d.numeroVolArrivee || '—',
                parcours: 'Transfert-Arrivée',
                client: clientInfo,
                guide: guideLabel,
                vehicule: vehiculeLabel,
                type: d.type,
                id: d.id,
                isMain: true
            });
        }

        // 2. Explosion des Excursions
        (d.excursions || []).forEach((ex, idx) => {
            rows.push({
                date: dateFormatter(ex.date),
                sortDate: ex.date,
                year: ex.date ? new Date(ex.date).getFullYear() : year,
                ref: d.reference,
                agence: agenceInfo,
                miseEnPlace: ex.observation || ex.lieu || '—',
                heure: ex.heure || '—',
                vol: '—',
                parcours: ex.type?.libelle || ex.excursion || 'Excursion',
                client: clientInfo,
                guide: getLabel(ex.guide, 'nomComplet') !== '—' ? getLabel(ex.guide, 'nomComplet') : guideLabel,
                vehicule: vehiculeLabel,
                type: d.type,
                id: d.id,
                subId: `ex-${idx}`
            });
        });

        // 3. Explosion des Transferts
        (d.transferts || []).forEach((tr, idx) => {
            rows.push({
                date: dateFormatter(tr.date),
                sortDate: tr.date,
                year: tr.date ? new Date(tr.date).getFullYear() : year,
                ref: d.reference,
                agence: agenceInfo,
                miseEnPlace: tr.lieuDepart || '—',
                heure: tr.heure || '—',
                vol: '—',
                parcours: `Transfert: ${tr.lieuDepart || '—'} -> ${tr.lieuArrivee || '—'}`,
                client: clientInfo,
                guide: getLabel(tr.guide, 'nomComplet') !== '—' ? getLabel(tr.guide, 'nomComplet') : guideLabel,
                vehicule: getLabel(tr.vehicule, 'matricule') !== '—' ? getLabel(tr.vehicule, 'matricule') : vehiculeLabel,
                type: d.type,
                id: d.id,
                subId: `tr-${idx}`
            });
        });

        // 4. Ligne de Départ
        if (d.dateVolDepart || d.heureVolDepart) {
            rows.push({
                date: dateFormatter(d.dateVolDepart || dateFin),
                sortDate: d.dateVolDepart || dateFin,
                year: d.dateVolDepart ? new Date(d.dateVolDepart).getFullYear() : (dateFin ? new Date(dateFin).getFullYear() : year),
                ref: d.reference,
                agence: agenceInfo,
                miseEnPlace: dernierDepart,
                heure: d.heureVolDepart ? dayjs(d.heureVolDepart).format('HH:mm') : '—',
                vol: d.numeroVolDepart || '—',
                parcours: 'Transfert-Départ',
                client: clientInfo,
                guide: guideLabel,
                vehicule: vehiculeLabel,
                type: d.type,
                id: d.id,
                isEnd: true
            });
        }

        // Si aucune ligne n'a été ajoutée, on met une ligne par défaut
        if (rows.filter(r => r.id === d.id).length === 0) {
            rows.push({
                date: dateDebut && dateFin && dateDebut !== dateFin ? `Du ${dateFormatter(dateDebut)} au ${dateFormatter(dateFin)}` : dateFormatter(dateDebut || d.date),
                sortDate: dateDebut || d.date,
                year,
                ref: d.reference,
                agence: agenceInfo,
                miseEnPlace: premiereArrivee,
                heure: '—',
                vol: '—',
                parcours: d.programme?.libelle || d.type?.toUpperCase() || '—',
                client: clientInfo,
                guide: guideLabel,
                vehicule: vehiculeLabel,
                type: d.type,
                id: d.id
            });
        }
    });

    // Filtrer les lignes logistiques par colonne
    const filteredRows = rows.filter(row => {
        let matchDate = true;
        if (filters.date) {
            if (filters.dateType === 'month' && row.sortDate) {
                matchDate = moment(row.sortDate).format('YYYY-MM') === filters.date;
            } else if (filters.dateType === 'week' && row.sortDate) {
                matchDate = moment(row.sortDate).format('YYYY-[W]WW') === filters.date;
            } else if (filters.dateType === 'date' && row.sortDate) {
                matchDate = moment(row.sortDate).format('YYYY-MM-DD') === filters.date;
            } else {
                matchDate = String(row.date || '').toLowerCase().includes(filters.date.toLowerCase().trim());
            }
        }
        if (!matchDate) return false;

        return Object.keys(filters).every(key => {
            if (key === 'date' || key === 'dateType') return true;
            const filterVal = (filters[key] || '').toLowerCase().trim();
            if (!filterVal) return true;
            const rowVal = String(row[key] || '').toLowerCase();
            return rowVal.includes(filterVal);
        });
    });

    // Trier par date
    filteredRows.sort((a, b) => new Date(b.sortDate) - new Date(a.sortDate));

    // Grouper par année
    const years = [...new Set(filteredRows.map(r => r.year))].filter(y => y).sort((a, b) => b - a);

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: '80vh' }}>
            <Table size="small" stickyHeader sx={{ 
                '& .MuiTableCell-root': { 
                    borderRight: '1px solid #e0e0e0',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1
                } 
            }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: COLORS.gray[100] }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>DATE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '10%', textAlign: 'center' }}>REF</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>AGENCE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>MISE EN PLACE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '5%', textAlign: 'center' }}>H/E</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '5%', textAlign: 'center' }}>VO</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>PARCOURS</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>NOM DU CLIENT</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>GUIDE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>VEHIC.</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ p: 0.5, verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                <select 
                                    style={{ width: '95%', fontSize: '0.7rem', padding: '2px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                    value={filters.dateType || 'text'}
                                    onChange={(e) => setFilters({ ...filters, dateType: e.target.value, date: '' })}
                                >
                                    <option value="text">Texte</option>
                                    <option value="month">Par Mois</option>
                                    <option value="week">Par Semaine</option>
                                    <option value="date">Date exacte</option>
                                </select>
                                <input 
                                    type={filters.dateType === 'text' ? 'text' : filters.dateType}
                                    style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                    placeholder="Filtrer..."
                                    value={filters.date || ''}
                                    onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                                />
                            </div>
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Filtrer..."
                                value={filters.ref}
                                onChange={(e) => setFilters({ ...filters, ref: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Filtrer..."
                                value={filters.agence}
                                onChange={(e) => setFilters({ ...filters, agence: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Filtrer..."
                                value={filters.miseEnPlace}
                                onChange={(e) => setFilters({ ...filters, miseEnPlace: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Flt..."
                                value={filters.heure}
                                onChange={(e) => setFilters({ ...filters, heure: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Flt..."
                                value={filters.vol}
                                onChange={(e) => setFilters({ ...filters, vol: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Filtrer..."
                                value={filters.parcours}
                                onChange={(e) => setFilters({ ...filters, parcours: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Filtrer..."
                                value={filters.client}
                                onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Filtrer..."
                                value={filters.guide}
                                onChange={(e) => setFilters({ ...filters, guide: e.target.value })}
                            />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                            <input 
                                style={{ width: '95%', fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}
                                placeholder="Flt..."
                                value={filters.vehicule}
                                onChange={(e) => setFilters({ ...filters, vehicule: e.target.value })}
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {years.map(year => (
                        <React.Fragment key={year}>
                            <TableRow>
                                <TableCell colSpan={10} sx={{ 
                                    bgcolor: '#f59e0b', 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    textAlign: 'center',
                                    py: 0.2,
                                    fontSize: '0.9rem',
                                    borderRight: 'none !important'
                                }}>
                                    {year}
                                </TableCell>
                            </TableRow>
                            {filteredRows.filter(r => r.year === year).map((row, idx) => (
                                <TableRow 
                                    key={`${row.id}-${row.subId || idx}`} 
                                    hover 
                                    sx={{ 
                                        cursor: 'pointer',
                                        bgcolor: row.isMain || row.isEnd ? alpha(COLORS.primary, 0.03) : 'inherit'
                                    }}
                                    onClick={() => navigateTo(`/dossiers/${row.type}/${row.id}`)}
                                >
                                    <TableCell sx={{ fontWeight: row.isMain || row.isEnd ? 600 : 400 }}>{row.date}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: COLORS.secondary }}>{row.ref}</TableCell>
                                    <TableCell>{row.agence}</TableCell>
                                    <TableCell>{row.miseEnPlace}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>{row.heure}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>{row.vol}</TableCell>
                                    <TableCell sx={{ fontStyle: row.subId ? 'italic' : 'normal' }}>{row.parcours}</TableCell>
                                    <TableCell>{row.client}</TableCell>
                                    <TableCell>{row.guide}</TableCell>
                                    <TableCell>{row.vehicule}</TableCell>
                                </TableRow>
                            ))}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const UnifiedCalendar = () => {
    const { allCircuits } = useSelector((state) => state.circuits);
    const dispatch = useDispatch();
    const [events, setEvents] = useState([]);
    const [viewTab, setViewTab] = useState(1);
    const navigateTo = useNavigate();

    useEffect(() => {
        // Charger tous les types de dossiers pour le calendrier
        dispatch(fetchCircuits({ cloture: false }));
    }, [dispatch]);

    useEffect(() => {
        if (!allCircuits) return;

        const _ev = [];
        allCircuits.forEach((dossier) => {
            const dateDebut = dossier.dateVolArrivee || dossier.dateDebut || dossier.date;
            const dateFin = dossier.dateVolDepart || dossier.dateFin || dossier.date;
            // 1. Le dossier lui-même (période globale)
            if (dateDebut && dateFin) {
                _ev.push({
                    id: `dos-${dossier.id}`,
                    start: new Date(dateDebut),
                    end: new Date(dateFin),
                    title: `[${dossier.type.toUpperCase()}] ${dossier.reference} - ${dossier.toClient?.libelle || dossier.nomClient || ''}`,
                    type: dossier.type,
                    dossierId: dossier.id,
                    dossierType: dossier.type
                });
            }

            // 2. Les excursions
            (dossier.excursions || []).forEach((ex, idx) => {
                if (ex.date) {
                    _ev.push({
                        id: `ex-${dossier.id}-${idx}`,
                        start: new Date(ex.date),
                        end: new Date(ex.date),
                        title: `EXC: ${ex.type?.libelle || 'Excursion'} (${dossier.reference})`,
                        type: 'excursion',
                        dossierId: dossier.id,
                        dossierType: dossier.type
                    });
                }
            });

            // 3. Les transferts
            (dossier.transferts || []).forEach((tr, idx) => {
                if (tr.date) {
                    _ev.push({
                        id: `tr-${dossier.id}-${idx}`,
                        start: new Date(tr.date),
                        end: new Date(tr.date),
                        title: `TRF: ${tr.lieuDepart} -> ${tr.lieuArrivee} (${dossier.reference})`,
                        type: 'transfert',
                        dossierId: dossier.id,
                        dossierType: dossier.type
                    });
                }
            });

            // 4. Les hôtels
            (dossier.hotels || []).forEach((h, idx) => {
                if (h.dateDebut && h.dateFin) {
                    _ev.push({
                        id: `ht-${dossier.id}-${idx}`,
                        start: new Date(h.dateDebut),
                        end: new Date(h.dateFin),
                        title: `HTL: ${h.hotel?.nom || 'Hôtel'} (${dossier.reference})`,
                        type: 'hotel',
                        dossierId: dossier.id,
                        dossierType: dossier.type
                    });
                }
            });
        });
        setEvents(_ev);
    }, [allCircuits]);

    const handleSelectEvent = (event) => {
        if (event.dossierId) {
            navigateTo(`/dossiers/${event.dossierType}/${event.dossierId}`);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: COLORS.primary }}>
                        Calendrier Logistique Unifié
                    </Typography>
                    
                    <Tabs 
                        value={viewTab} 
                        onChange={(e, v) => setViewTab(v)}
                        sx={{ 
                            bgcolor: alpha(COLORS.primary, 0.05),
                            borderRadius: 2,
                            p: 0.5,
                            '& .MuiTabs-indicator': { display: 'none' },
                            '& .MuiTab-root': { 
                                borderRadius: 1.5, 
                                minHeight: 36,
                                py: 1,
                                px: 3,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&.Mui-selected': { 
                                    bgcolor: COLORS.primary, 
                                    color: 'white' 
                                }
                            }
                        }}
                    >
                        <Tab icon={<EventIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Vue Calendrier" />
                        <Tab icon={<ListIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Vue Logistique (Tableau)" />
                    </Tabs>
                </Box>
                
                {viewTab === 0 ? (
                    <>
                        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label="Circuits" size="small" sx={{ bgcolor: '#1e3a8a', color: 'white' }} />
                            <Chip label="Séjours" size="small" sx={{ bgcolor: '#d97706', color: 'white' }} />
                            <Chip label="Excursions" size="small" sx={{ bgcolor: '#059669', color: 'white' }} />
                            <Chip label="Transferts" size="small" sx={{ bgcolor: '#7c3aed', color: 'white' }} />
                            <Chip label="Hôtels" size="small" sx={{ bgcolor: '#db2777', color: 'white' }} />
                        </Box>

                        <Box sx={{ height: '75vh' }}>
                            <Calendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                style={{ height: '100%' }}
                                messages={messages}
                                culture="fr"
                                eventPropGetter={eventPropGetter}
                                onSelectEvent={handleSelectEvent}
                                tooltipAccessor={event => event.title}
                            />
                        </Box>
                    </>
                ) : (
                    <LogisticsView circuits={allCircuits || []} />
                )}
            </Paper>
        </Box>
    );
};

export default UnifiedCalendar;
